<template>
	<Transition name="Modal">
		<MobileModal v-if="modalState.isOpen" @close="handleCloseAll">
			<KeepAlive>
				<component
					:is="modalState.component"
					v-bind="modalState.props"
					@close="handleClose"
				/>
			</KeepAlive>
		</MobileModal>
	</Transition>
</template>

<script setup lang="ts">
import { nextTick, watch } from 'vue';
import { useMobileModal, useModalBreakpoint } from '@composables/useMobileModal';

const { modalState, close, closeAll } = useMobileModal();

useModalBreakpoint();

let scrollPosition = 0;
let savedBodyStyles: Record<string, string> = {
	overflow: '',
	position: '',
	top: '',
	width: ''
};

const saveBodyStyles = (): void => {
	savedBodyStyles = {
		overflow: document.body.style.overflow,
		position: document.body.style.position,
		top: document.body.style.top,
		width: document.body.style.width
	};
};

const restoreBodyStyles = (): void => {
	const { overflow, position, top, width } = savedBodyStyles;

	document.body.style.overflow = overflow;
	document.body.style.position = position;
	document.body.style.top = top;
	document.body.style.width = width;
};

const lockBodyScroll = (): void => {
	scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
	saveBodyStyles();

	document.body.style.overflow = 'hidden';
	document.body.style.position = 'fixed';
	document.body.style.top = `-${scrollPosition}px`;
	document.body.style.width = '100%';
};

const unlockBodyScroll = (): void => {
	restoreBodyStyles();

	window.scrollTo(0, scrollPosition);
};

const scrollContentToTop = (): void => {
	const content = document.querySelector<HTMLElement>('.MobileModal .Content');
	if (!content) return;

	content.scrollTop = 0;
};

watch(() => modalState.value.isOpen, (isOpen) => {
	if (isOpen) {
		lockBodyScroll();
		return;
	}

	unlockBodyScroll();
});

watch(() => modalState.value.component, () => {
	void nextTick(() => scrollContentToTop());
});


const handleClose = (): void => {
	close();
};

const handleCloseAll = (): void => {
	closeAll();
};
</script>

<style scoped lang="scss">
	.Modal {
		&-enter-active {
			transition: transform 0.3s ease-out;
		}

		&-leave-active {
			transition: transform 0.3s ease-in;
		}

		&-enter-from {
			transform: translateY(100%);
		}

		&-leave-to {
			transform: translateY(100%);
		}
	}
</style>
